import React from "react";
import { useClientSideSplitTreatments } from "@leafly-com/split-next";
import { gtmHeadTag } from "@leafly-com/web-utils";
import dynamic from "next/dynamic";
import Script from "next/script";
import ProgressBar from "nextjs-toploader";

import { SnackBarProvider } from "context/SnackBarContext";
import { ToastFrame } from "context/Toast/ToastFrame";
import useCoreWebVitals from "hooks/useCoreWebVitals";
import { getSplitKey } from "utils/split/getSplitKey";

import AgeGate from "components/AgeGate/AgeGate";
import { ModalFrame } from "components/botanic/Modal";
import Footer from "components/Footer/Footer";
import { GlobalLoadingSpinner } from "components/GlobalLoadingSpinner/GlobalLoadingSpinner";
import { GooglePublisherTagScript } from "components/GooglePublisherTagScript";
import { Header } from "components/Header";
import { WeedfettiCannon } from "components/Weedfetti";

import { AppSideEffects } from "./AppSideEffects";

const ChatwootWidget = dynamic(() => import("components/ChatwootWidget"), {
  ssr: false,
});

type Props = {
  children?: React.ReactNode;
  disableFixedHeader?: boolean;
  disableHeaderCollapseOnScroll?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
  isAppBannerCookieDismissed?: boolean;
  showMinimalHeader?: boolean;
};

const Layout: React.FC<Props> = ({
  children,
  disableFixedHeader,
  disableHeaderCollapseOnScroll,
  hideFooter,
  hideHeader,
  isAppBannerCookieDismissed,
  showMinimalHeader,
}) => {
  const { isLoading, treatments } = useClientSideSplitTreatments(
    ["chatwoot_flag"],
    getSplitKey()
  );

  useCoreWebVitals();

  return (
    <ModalFrame>
      <Script
        async
        id="google-tag-manager"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: gtmHeadTag }}
      />
      <GooglePublisherTagScript />

      {!hideHeader && (
        <Header
          disableFixedPosition={disableFixedHeader}
          disableScrollCollapse={disableHeaderCollapseOnScroll}
          isAppBannerCookieDismissed={isAppBannerCookieDismissed}
          minimal={showMinimalHeader}
        />
      )}
      <AgeGate />
      <AppSideEffects />
      <GlobalLoadingSpinner />
      <ProgressBar
        color="var(--color-green)"
        initialPosition={0.3}
        crawlSpeed={25}
        showSpinner={false}
        shadow="0"
      />

      <ToastFrame>
        <SnackBarProvider>
          <main>{children}</main>
          {!hideFooter && <Footer />}
        </SnackBarProvider>
      </ToastFrame>

      {!isLoading && treatments.chatwoot_flag.treatment === "on" && (
        <ChatwootWidget />
      )}

      <WeedfettiCannon />
    </ModalFrame>
  );
};

export default Layout;
